import React, { useRef, useEffect, useCallback } from 'react';
import { Paperclip, Send, Mic, Square, X, Sparkles } from 'lucide-react';

interface ChatInputProps {
  inputText: string;
  setInputText: (text: string) => void;
  fileToUpload: File | null;
  onFileClick: () => void;
  onFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onSendMessage: () => void;
  isRecording: boolean;
  onToggleRecording: () => void;
  acceptedFileTypes: string;
  disabled?: boolean;
  onClearFile?: () => void;
  modeLabel?: string | null;
  placeholder?: string;
  onClearMode?: () => void;
}

export const ChatInput: React.FC<ChatInputProps> = ({
  inputText,
  setInputText,
  fileToUpload,
  onFileClick,
  onFileChange,
  onSendMessage,
  isRecording,
  onToggleRecording,
  acceptedFileTypes,
  disabled = false,
  onClearFile,
  modeLabel,
  placeholder,
  onClearMode
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-resize textarea based on content (ChatGPT style)
  const adjustTextareaHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    // Reset height to auto to get the correct scrollHeight
    textarea.style.height = 'auto';
    
    // Calculate new height (min 52px for single line, max 200px)
    const minHeight = 52;
    const maxHeight = 200;
    const scrollHeight = textarea.scrollHeight;
    
    const newHeight = Math.min(Math.max(scrollHeight, minHeight), maxHeight);
    textarea.style.height = `${newHeight}px`;
    
    // Show scrollbar only when content exceeds max height
    textarea.style.overflowY = scrollHeight > maxHeight ? 'auto' : 'hidden';
  }, []);

  // Adjust height whenever input text changes
  useEffect(() => {
    adjustTextareaHeight();
  }, [inputText, adjustTextareaHeight]);

  // Focus textarea on mount
  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter') {
      if (e.shiftKey) {
        // Allow new line with Shift+Enter - let default behavior happen
        return;
      } else {
        // Send message on Enter (without Shift)
        e.preventDefault();
        if (inputText.trim() || fileToUpload) {
          onSendMessage();
        }
      }
    }
  };

  const handleFileClick = () => {
    fileInputRef.current?.click();
  };

  const handleClearFile = () => {
    if (onClearFile) {
      onClearFile();
    }
    // Also clear the file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const canSend = !disabled && (inputText.trim() || fileToUpload);
  const placeholderText = fileToUpload ? 'Add a message...' : (placeholder || 'Type your message...');

  return (
    <div data-tour="chat-input" className="w-full">
      {/* Mode chip */}
      {modeLabel && (
        <div className="mb-2 flex items-center justify-between gap-3">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[var(--accent-primary)]/10 border border-[var(--border-color)] text-xs text-[var(--text-primary)]">
            <Sparkles className="h-3.5 w-3.5 text-[var(--accent-primary)]" />
            <span className="font-medium">{modeLabel}</span>
          </div>
          {onClearMode && (
            <button
              type="button"
              onClick={onClearMode}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] transition-colors"
              aria-label="Clear mode"
            >
              <X className="h-3.5 w-3.5" />
              Clear
            </button>
          )}
        </div>
      )}

      {/* File preview - shown above the input */}
      {fileToUpload && (
        <div className="mb-2 p-3 bg-[var(--surface-elevated)] rounded-xl border border-[var(--border-color)] animate-in slide-in-from-bottom-2 duration-200">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="p-2 bg-[var(--accent-primary)]/10 rounded-lg">
                <Paperclip className="h-4 w-4 text-[var(--accent-primary)]" />
              </div>
              <div className="min-w-0">
                <p className="text-sm text-[var(--text-primary)] truncate font-medium">
                  {fileToUpload.name}
                </p>
                <p className="text-xs text-[var(--text-secondary)]">
                  {(fileToUpload.size / 1024 / 1024).toFixed(2)} MB
                </p>
              </div>
            </div>
            <button
              onClick={handleClearFile}
              className="p-1.5 hover:bg-[var(--hover-bg)] rounded-lg transition-colors text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
              aria-label="Remove file"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Main input container - ChatGPT style */}
      <div className="relative bg-[var(--surface-elevated)] border border-[var(--border-color)] rounded-2xl shadow-lg transition-all focus-within:border-[var(--accent-primary)]/50 focus-within:shadow-[0_0_0_2px_rgba(var(--accent-primary-rgb),0.1)]">
        {/* Recording indicator */}
        {isRecording && (
          <div className="absolute -top-2 left-1/2 -translate-x-1/2 px-3 py-1 bg-red-500 text-white text-xs font-medium rounded-full animate-pulse flex items-center gap-1.5">
            <span className="w-2 h-2 bg-white rounded-full animate-ping" />
            Recording...
          </div>
        )}

        <div className="flex items-end gap-1 p-2">
          {/* Attachment button */}
          <input
            type="file"
            ref={fileInputRef}
            onChange={onFileChange}
            accept={acceptedFileTypes}
            className="hidden"
          />
          <button 
            onClick={handleFileClick}
            disabled={disabled}
            className="flex-shrink-0 p-2.5 hover:bg-[var(--hover-bg)] rounded-xl transition-colors text-[var(--text-secondary)] hover:text-[var(--accent-primary)] disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Attach file"
          >
            <Paperclip className="h-5 w-5" />
          </button>
          
          {/* Expandable textarea */}
          <div className="flex-1 min-w-0">
            <textarea
              ref={textareaRef}
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={placeholderText}
              disabled={disabled}
              className="w-full bg-transparent border-none focus:outline-none text-[var(--text-primary)] placeholder-[var(--text-secondary)] resize-none py-3 px-2 text-base leading-6 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                minHeight: '52px',
                maxHeight: '200px',
              }}
              rows={1}
            />
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-1 flex-shrink-0">
            {/* Voice recording button */}
            <button 
              onClick={onToggleRecording}
              disabled={disabled}
              className={`p-2.5 rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                isRecording 
                  ? 'text-white bg-red-500 hover:bg-red-600 shadow-lg shadow-red-500/30' 
                  : 'text-[var(--text-secondary)] hover:bg-[var(--hover-bg)] hover:text-[var(--text-primary)]'
              }`}
              aria-label={isRecording ? 'Stop recording' : 'Start voice recording'}
            >
              {isRecording ? (
                <Square className="h-5 w-5" fill="currentColor" />
              ) : (
                <Mic className="h-5 w-5" />
              )}
            </button>
            
            {/* Send button */}
            <button
              onClick={() => onSendMessage()}
              disabled={!canSend}
              className={`p-2.5 rounded-xl transition-all ${
                canSend
                  ? 'bg-[var(--accent-primary)] text-white hover:opacity-90 shadow-lg shadow-[var(--accent-primary)]/30'
                  : 'bg-[var(--hover-bg)] text-[var(--text-secondary)] cursor-not-allowed'
              }`}
              aria-label="Send message"
            >
              <Send className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Helper text */}
        <div className="px-4 pb-2 pt-0">
          <p className="text-[10px] text-[var(--text-secondary)]/60 text-center">
            Press Enter to send, Shift+Enter for new line
          </p>
        </div>
      </div>
    </div>
  );
};
